import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaAndroid,
  FaApple,
  FaCheckCircle,
  FaCity,
  FaComments,
  FaFacebook,
  FaHandshake,
  FaInstagram,
  FaMapMarkedAlt,
  FaRegClock,
  FaSearch,
  FaShieldAlt,
  FaUsers,
} from "react-icons/fa";

function Home() {
  const navigate = useNavigate();
  const [showStoreChooser, setShowStoreChooser] = useState(false);
  const [heroSearch, setHeroSearch] = useState({
    city: "",
    minPrice: "",
    maxPrice: "",
  });

  const googlePlayUrl =
    "https://play.google.com/store/apps/details?id=com.yourcompany.rentalhighway&hl=en_US&utm_source=website&utm_medium=hero&utm_campaign=np_launch";
  const appStoreUrl =
    "https://apps.apple.com/gb/app/rental-higway/id6756333874?utm_source=website&utm_medium=hero&utm_campaign=np_launch";
  const qrGooglePlayUrl =
    "https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=" +
    encodeURIComponent(googlePlayUrl);
  const qrAppStoreUrl =
    "https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=" +
    encodeURIComponent(appStoreUrl);

  const getAttributionData = () => {
    if (typeof window === "undefined") {
      return {
        utm_source: "(direct)",
        utm_medium: "(none)",
        utm_campaign: "(not_set)",
      };
    }

    const params = new URLSearchParams(window.location.search);

    return {
      utm_source: params.get("utm_source") || "(direct)",
      utm_medium: params.get("utm_medium") || "(none)",
      utm_campaign: params.get("utm_campaign") || "(not_set)",
    };
  };

  const trackInstallClick = (platform, placement) => {
    if (typeof window === "undefined") {
      return;
    }

    const eventPayload = {
      event_category: "app_install",
      event_label: `${placement}:${platform}`,
      cta_type: "app_install",
      platform,
      placement,
      device_type: getDeviceType(),
      page_path: window.location.pathname,
      ...getAttributionData(),
    };

    if (typeof window.gtag === "function") {
      window.gtag("event", "app_install_click", eventPayload);
    }

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: "app_install_click",
        ...eventPayload,
      });
    }
  };

  const openStore = (platform, placement) => {
    const targetUrl = platform === "ios" ? appStoreUrl : googlePlayUrl;
    trackInstallClick(platform, placement);
    window.open(targetUrl, "_blank", "noopener,noreferrer");
  };

  const getDeviceType = () => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return "desktop";
    }

    const ua = navigator.userAgent || navigator.vendor || window.opera || "";
    const isiOS =
      /iPad|iPhone|iPod/i.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/i.test(ua);

    if (isiOS) {
      return "ios";
    }

    if (isAndroid) {
      return "android";
    }

    return "desktop";
  };

  useEffect(() => {
    if (!showStoreChooser) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowStoreChooser(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showStoreChooser]);

  const handleNavDownload = (event) => {
    event.preventDefault();
    const deviceType = getDeviceType();

    if (deviceType === "ios") {
      openStore("ios", "nav_button");
      return;
    }

    if (deviceType === "android") {
      openStore("android", "nav_button");
      return;
    }

    trackInstallClick("unknown", "nav_button_store_chooser");
    setShowStoreChooser(true);
  };

  const provinceCoverage = [
    "Koshi Province",
    "Madhesh Province",
    "Bagmati Province",
    "Gandaki Province",
    "Lumbini Province",
    "Karnali Province",
    "Sudurpashchim Province",
  ];

  const onHeroSearchChange = (event) => {
    const { name, value } = event.target;
    setHeroSearch((prev) => ({ ...prev, [name]: value }));
  };

  const handleHeroSearchLogin = () => {
    const params = new URLSearchParams();
    if (heroSearch.city.trim()) params.set("city", heroSearch.city.trim());
    if (heroSearch.minPrice.trim())
      params.set("minPrice", heroSearch.minPrice.trim());
    if (heroSearch.maxPrice.trim())
      params.set("maxPrice", heroSearch.maxPrice.trim());

    const query = params.toString();
    navigate(query ? `/app/login?${query}` : "/app/login");
  };

  return (
    <div className="home-page">
      <header className="header">
        <div className="container">
          <Link className="brand" to="/" aria-label="Rental Highway Home">
            <img
              src="/assets/logo/LogoH.png"
              alt="Rental Highway"
              className="brand-logo"
            />
          </Link>
          <nav className="nav-links" aria-label="Main Navigation">
            <a href="#why-us">Why Us</a>
            <a href="#coverage">All Nepal Coverage</a>
            <a href="#for-everyone">For Renters & Owners</a>
            <Link to="/blog">Blogs</Link>
          </nav>
          <div className="header-actions">
            <Link className="btn btn-primary btn-nav" to="/app/login">
              Find Rental
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section className="hero" id="top">
          <div className="gradient-orb orb-a" aria-hidden="true" />
          <div className="gradient-orb orb-b" aria-hidden="true" />
          <div className="container hero-grid">
            <div className="hero-copy">
              <p className="eyebrow">
                Nepal's rental app, built for speed and trust
              </p>
              <h1>
                Your next home —<br className="hero-break-desktop" /> or your
                next tenant —<br /> is already here.
              </h1>
              <p className="hero-tagline">
                Browse hundreds of verified listings across all seven provinces
                — with exact map locations, direct owner contact, and zero
                middlemen. Available on the web and as a mobile app.
              </p>
              <div
                className="unified-search-bar home-hero-search-bar"
                aria-label="Search teaser"
              >
                <div className="search-input-group search-city-group">
                  <label className="search-input-label">
                    <input
                      name="city"
                      value={heroSearch.city}
                      onChange={onHeroSearchChange}
                      placeholder="City Name..."
                      className="search-input-field"
                    />
                  </label>
                </div>
                <div className="search-bar-divider" />
                <div className="search-input-group search-price-group">
                  <label className="search-input-label">
                    <input
                      name="minPrice"
                      type="number"
                      value={heroSearch.minPrice}
                      onChange={onHeroSearchChange}
                      placeholder="Min(Rs.)"
                      className="search-input-field"
                    />
                  </label>
                </div>
                <div className="search-bar-divider" />
                <div className="search-input-group search-price-group">
                  <label className="search-input-label">
                    <input
                      name="maxPrice"
                      type="number"
                      value={heroSearch.maxPrice}
                      onChange={onHeroSearchChange}
                      placeholder="Max(Rs.)"
                      className="search-input-field"
                    />
                  </label>
                </div>
                <button
                  type="button"
                  className="btn btn-search"
                  onClick={handleHeroSearchLogin}
                  aria-label="Search"
                >
                  <FaSearch className="btn-search-icon" aria-hidden="true" />
                  <span className="btn-search-text">Search</span>
                </button>
              </div>
              <div className="store-links" aria-label="Store badges">
                <a
                  href={googlePlayUrl}
                  title="Get it on Google Play"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackInstallClick("android", "hero_badge")}
                >
                  <img
                    src="assets/logo/google-play-store.png"
                    alt="Google Play"
                    loading="eager"
                    decoding="async"
                  />
                </a>
                <a
                  href={appStoreUrl}
                  title="Download on the App Store"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackInstallClick("ios", "hero_badge")}
                >
                  <img
                    src="assets/logo/iphone-app-store.png"
                    alt="App Store"
                    loading="eager"
                    decoding="async"
                  />
                </a>
              </div>
            </div>

            <aside className="hero-media" aria-label="App preview">
              <div className="phone-shell">
                <img
                  src="assets/images/screenshots/1.png"
                  alt="Rental Highway app listing view"
                />
              </div>
            </aside>
          </div>
        </section>

        <section className="kpi-strip section-padding">
          <div className="container kpi-grid">
            <article>
              <h3 className="kpi-title">
                <FaShieldAlt /> Verified First
              </h3>
              <p>Identity and listing checks for safer renting.</p>
            </article>
            <article>
              <h3 className="kpi-title">
                <FaRegClock /> Faster Matches
              </h3>
              <p>
                Reduce listing-to-inquiry time with a focused mobile workflow.
              </p>
            </article>
            <article>
              <h3 className="kpi-title">
                <FaCity /> Built For Nepal
              </h3>
              <p>
                Built for local rental needs from major cities to remote
                communities.
              </p>
            </article>
          </div>
        </section>

        <section className="section-padding desktop-qr-section">
          <div className="container">
            <h2>Download directly from your desktop</h2>
            <p className="section-tagline">
              Scan with your phone camera and continue instantly in the Rental
              Highway app.
            </p>
            <div className="qr-grid">
              <article className="qr-card">
                <img
                  src={qrGooglePlayUrl}
                  alt="QR code for Google Play download"
                  className="qr-image"
                  loading="lazy"
                />
                <a
                  className="btn btn-secondary"
                  href={googlePlayUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    trackInstallClick("android", "desktop_qr_section")
                  }
                >
                  <FaAndroid /> Google Play
                </a>
              </article>
              <article className="qr-card">
                <img
                  src={qrAppStoreUrl}
                  alt="QR code for App Store download"
                  className="qr-image"
                  loading="lazy"
                />
                <a
                  className="btn btn-secondary"
                  href={appStoreUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackInstallClick("ios", "desktop_qr_section")}
                >
                  <FaApple /> App Store
                </a>
              </article>
            </div>
          </div>
        </section>

        <section id="why-us" className="section-padding">
          <div className="container">
            <h2>Why Rental Highway wins in Nepal</h2>
            <p className="section-tagline">
              Landlords with no way to reach good tenants. Renters with no way
              to trust what they were seeing. Rental Highway fixes both sides of
              that problem — transparently, for everyone.
            </p>

            <div className="features-grid">
              <article className="feature-card">
                <FaMapMarkedAlt className="feature-icon" />
                <h4>Exact map location, every listing</h4>
                <p>
                  See where the property sits before you pick up the phone. No
                  more wasted trips or misleading descriptions.
                </p>
              </article>

              <article className="feature-card">
                <FaHandshake className="feature-icon" />
                <h4>Verified listings, real trust</h4>
                <p>
                  A verification-first approach improves confidence for both
                  renters and owners from the first interaction.
                </p>
              </article>

              <article className="feature-card">
                <FaUsers className="feature-icon" />
                <h4>One platform, both sides</h4>
                <p>
                  Whether you're hunting for a flat or filling a vacancy, Rental
                  Highway balances demand and supply across all seven provinces
                  — on web and mobile.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section id="coverage" className="section-padding city-section">
          <div className="container">
            <h2>Wherever you are in Nepal, you belong here.</h2>
            <p className="section-tagline">
              From the valleys of Bagmati to the far west, Rental Highway is
              building equal rental access for every community — not just the
              big cities.
            </p>
            <div
              className="city-chips"
              role="list"
              aria-label="Coverage across Nepal"
            >
              {provinceCoverage.map((province) => (
                <span key={province} role="listitem" className="city-chip">
                  <FaMapMarkedAlt /> {province}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section id="for-everyone" className="section-padding split-benefits">
          <div className="container split-grid">
            <article className="benefit-panel">
              <h3>For Renters: Find a home without the runaround.</h3>
              <ul>
                <li>
                  <FaCheckCircle /> Discover verified rooms, flats, apartments,
                  and commercial spaces.
                </li>
                <li>
                  <FaCheckCircle /> Use exact map location to shortlist nearby
                  options faster.
                </li>
                <li>
                  <FaCheckCircle /> Contact owners directly and move at your own
                  pace, with no broker pressure.
                </li>
              </ul>
            </article>

            <article className="benefit-panel">
              <h3>For Property Owners: List once. Reach thousands.</h3>
              <ul>
                <li>
                  <FaCheckCircle /> List properties quickly and reach active
                  renters across provinces.
                </li>
                <li>
                  <FaCheckCircle /> Handle inquiries from the comfort of your
                  phone from anywhere.
                </li>
                <li>
                  <FaCheckCircle /> Build credibility with detailed listings and
                  transparent property information.
                </li>
              </ul>
            </article>
          </div>
        </section>

        <section
          className="section-padding app-preview"
          aria-label="App Screenshots"
        >
          <div className="container">
            <h2>Preview the app experience</h2>
            <p className="section-tagline">
              Simple navigation, map-first discovery, and fast actions from
              listing to contact.
            </p>
            <div className="app-screenshots">
              <img
                src="assets/images/screenshots/1.png"
                alt="App Screenshot 1"
                className="app-screenshot-placeholder"
                loading="lazy"
                decoding="async"
              />
              <img
                src="assets/images/screenshots/2.png"
                alt="App Screenshot 2"
                className="app-screenshot-placeholder"
                loading="lazy"
                decoding="async"
              />
              <img
                src="assets/images/screenshots/4.png"
                alt="App Screenshot 3"
                className="app-screenshot-placeholder"
                loading="lazy"
                decoding="async"
              />
              <img
                src="assets/images/screenshots/5.png"
                alt="App Screenshot 4"
                className="app-screenshot-placeholder"
                loading="lazy"
                decoding="async"
              />
              <img
                src="assets/images/screenshots/6.png"
                alt="App Screenshot 5"
                className="app-screenshot-placeholder"
                loading="lazy"
                decoding="async"
              />
              <img
                src="assets/images/screenshots/7.png"
                alt="App Screenshot 6"
                className="app-screenshot-placeholder"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </section>

        <section id="faq" className="section-padding faq-section">
          <div className="container faq-wrap">
            <h2>Frequently asked questions</h2>
            <details>
              <summary>Can I search property directly on this website?</summary>
              <p>
                Yes. You can either use the webapp or the mobile app to search
                for properties.
              </p>
            </details>
            <details>
              <summary>How do I search rentals near me?</summary>
              <p>
                Install Rental Highway from Google Play or the App Store or just
                use the webapp from the top right corner, choose your location,
                and browse verified listings with exact map visibility.
              </p>
            </details>
            <details>
              <summary>Is listing a property free for owners?</summary>
              <p>
                Yes. Owners can list properties in the app and start receiving
                renter inquiries from across Nepal.
              </p>
            </details>
          </div>
        </section>

        <section className="section-padding final-cta">
          <div className="container final-cta-box">
            <h2>
              Join thousands of Nepalis who found their next home on Rental
              Highway.
            </h2>
            <p>
              Available now — as a mobile app on Android and iPhone, and as a
              web app you can open right now in your browser.
            </p>
            <div className="cta-row">
              <a
                className="btn btn-secondary"
                href={googlePlayUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackInstallClick("android", "final_banner")}
              >
                <FaAndroid /> Download for Android
              </a>
              <a
                className="btn btn-secondary"
                href={appStoreUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackInstallClick("ios", "final_banner")}
              >
                <FaApple /> Download for iPhone
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container">
          <div className="social-links">
            <a
              href="https://www.facebook.com/profile.php?id=61583854052645"
              aria-label="Facebook"
              target="_blank"
              rel="noreferrer"
            >
              <FaFacebook />
            </a>
            <a
              href="https://www.instagram.com/rental.highway/"
              aria-label="Instagram"
              target="_blank"
              rel="noreferrer"
            >
              <FaInstagram />
            </a>
            <a
              href="mailto:support@rentalhighway.com"
              aria-label="Email support"
            >
              <FaComments />
            </a>
          </div>
          <div className="footer-links">
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/data-deletion">Delete Account / Data</Link>
            <a href="mailto:support@rentalhighway.com">Contact Us</a>
          </div>
          <div className="footer-copyright">
            &copy; {new Date().getFullYear()} Rental Highway. Built for Nepal.
            Every province. Every renter. Every owner.
          </div>
        </div>
      </footer>

      <div
        className="mobile-install-bar"
        role="complementary"
        aria-label="Install app"
      >
        <a
          className="btn btn-primary"
          href={googlePlayUrl}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackInstallClick("android", "mobile_sticky_bar")}
        >
          <FaAndroid /> Android
        </a>
        <a
          className="btn btn-secondary"
          href={appStoreUrl}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackInstallClick("ios", "mobile_sticky_bar")}
        >
          <FaApple /> iPhone
        </a>
      </div>

      {showStoreChooser && (
        <div
          className="store-chooser-backdrop"
          role="presentation"
          onClick={() => setShowStoreChooser(false)}
        >
          <div
            className="store-chooser-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="store-chooser-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="store-chooser-title">Choose your device</h3>
            <p>Download Rental Highway from your preferred app store.</p>
            <div className="store-chooser-actions">
              <a
                className="btn btn-primary"
                href={googlePlayUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  trackInstallClick("android", "store_chooser_modal");
                  setShowStoreChooser(false);
                }}
              >
                <FaAndroid /> Android
              </a>
              <a
                className="btn btn-secondary"
                href={appStoreUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  trackInstallClick("ios", "store_chooser_modal");
                  setShowStoreChooser(false);
                }}
              >
                <FaApple /> iPhone
              </a>
            </div>
            <button
              type="button"
              className="store-chooser-close"
              onClick={() => setShowStoreChooser(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;
